import { useState, useEffect, useMemo } from "react";
import { AppointmentData, AppointmentStatus, changeAppointmentStatus, fetchAppointmentById} from "@/services/appointment-service";
import { fetchDentists, Dentist} from "@/services/dentist-service";
import { Patient, CreatePatientData, updatePatient, fetchRegistryReviewConfig } from "@/services/patient-service";
import { createAuthorizationRequest } from "@/services/insurance-authorization-service";
import { RegistryReviewGateDialog } from "@/components/appointments/RegistryReviewGateDialog";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { statusLabels } from "@/utils/appointmentStatus";
import { Copy, Edit, Loader2, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import { InsuranceSelect } from "@/components/shared/InsuranceSelect";
import { formatCPF, formatPhone } from "@/utils/formatters";
import { CreateEditPatientDialog } from "@/components/patients/CreateEditPatientDialog";

interface EditAppointmentDialogProps {
  appointment: AppointmentData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (appointment: AppointmentData) => void;
}

export function EditAppointmentDialog({
  appointment,
  open,
  onOpenChange,
  onSave,
}: EditAppointmentDialogProps) {
  const [status, setStatus] = useState<AppointmentStatus>("booked");
  const [dentistId, setDentistId] = useState<string>("");
  const [insuranceId, setInsuranceId] = useState<string>("");
  const [cardNumber, setCardNumber] = useState<string>("");
  const [observation, setObservation] = useState<string>("");
  const [dentists, setDentists] = useState<Dentist[]>([]);
  const [loadedAppointment, setLoadedAppointment] = useState<AppointmentData | null>(null);
  const [editPatientDialogOpen, setEditPatientDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Autorização
  const [authExpanded, setAuthExpanded] = useState(false);
  const [authDescription, setAuthDescription] = useState("");
  const [isSendingAuth, setIsSendingAuth] = useState(false);

  // Gate de revisão de cadastro (paciente atendido com cadastro vencido)
  const [reviewDaysInterval, setReviewDaysInterval] = useState<number | null>(null);
  const [reviewGateOpen, setReviewGateOpen] = useState(false);
  const [pendingAttendedSave, setPendingAttendedSave] = useState(false);

  const currentAppointment = loadedAppointment ?? appointment;

  useEffect(() => {
    const base = loadedAppointment ?? appointment;
    if (base) {
      setStatus(base.appointment_status);
      setDentistId(base.dentist_id);
      setInsuranceId(base.appointment_dental_insurance_id || "");
      setCardNumber(base.appointment_card_number || base.patient_card_number || base.card_number || "");
      setObservation(base.observation || base.observations || "");
    }
  }, [appointment, loadedAppointment]);

  useEffect(() => {
    if (!open) return;
    if (reviewDaysInterval !== null) return;
    fetchRegistryReviewConfig()
      .then(cfg => setReviewDaysInterval(cfg.days_interval))
      .catch(() => setReviewDaysInterval(60));
  }, [open, reviewDaysInterval]);

  // Dias restantes até o cadastro vencer (negativo = já vencido).
  // null se ainda não temos dado suficiente.
  const reviewDaysRemaining: number | null = (() => {
    if (reviewDaysInterval === null) return null;
    if (!currentAppointment) return null;
    const last = currentAppointment.patient_last_registry_review_at;
    if (!last) return -1; // nunca revisado → vencido
    const reviewedAt = new Date(last).getTime();
    if (Number.isNaN(reviewedAt)) return -1;
    const expiresAt = reviewedAt + reviewDaysInterval * 24 * 60 * 60 * 1000;
    const msRemaining = expiresAt - Date.now();
    return Math.ceil(msRemaining / (24 * 60 * 60 * 1000));
  })();

  const isReviewExpired = reviewDaysRemaining !== null && reviewDaysRemaining <= 0;
  const isReviewExpiringSoon =
    reviewDaysRemaining !== null && reviewDaysRemaining > 0 && reviewDaysRemaining <= 10;

  useEffect(() => {
    if (!open) return;

    loadDentists();

    if (appointment?.id) {
      const loadAppointment = async () => {
        setIsLoading(true);
        try {
          const data = await fetchAppointmentById(appointment.id);
          setLoadedAppointment(data);
        } catch (error) {
          console.error("Erro ao carregar agendamento:", error);
          toast({
            title: "Erro ao carregar agendamento",
            description: error instanceof Error ? error.message : "Tente novamente.",
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
        }
      };

      loadAppointment();
    }
  }, [open, appointment]);

  const loadDentists = async () => {
    try {
      const data = await fetchDentists(false, false, undefined, 1, 100);
      setDentists(data);
    } catch (error) {
      toast({ title: "Erro ao carregar dentistas", variant: "destructive" });
    }
  };

  const copyToClipboard = (text: string, title: string = "Copiado!") => {
    navigator.clipboard.writeText(text);
    toast({
      title: title,
      description: "Conteúdo copiado para a área de transferência.",
    });
  };

  const handleCopyPatientData = () => {
    if (!currentAppointment) return;

    const formattedDate = currentAppointment.patient_birth_date 
      ? currentAppointment.patient_birth_date.split('-').reverse().join('/') 
      : 'Não informado';

    const card = currentAppointment.patient_card_number || currentAppointment.appointment_card_number || currentAppointment.card_number || "Não informado";

    const textToCopy = [
      `${currentAppointment.patient_name}`,
      `Data de Nascimento: ${formattedDate}`,
      `Telefone: ${formatPhone(currentAppointment.patient_cellphone) || 'Não informado'}`,
      `Convênio: ${currentAppointment.patient_dental_insurance_name || 'Não informado'}`,
      `Carteirinha: ${card}`,
      `CPF: ${formatCPF(currentAppointment.patient_cpf) || 'Não informado'}`
    ].join('\n');

    copyToClipboard(textToCopy, "Dados do Paciente Copiados!");
  };

  const persistStatusChange = async () => {
    if (!currentAppointment) return;

    setIsLoading(true);
    try {
      await changeAppointmentStatus(currentAppointment.id, {
        patient_id: currentAppointment.patient_id,
        new_status: status,
        dentist_id: dentistId,
        dental_insurance_id: insuranceId || "",
        card_number: cardNumber,
        observations: observation,
      });

      toast({
        title: "Sucesso!",
        description: "As alterações foram salvas com sucesso.",
      });

      onSave({
        ...currentAppointment,
        appointment_status: status,
        dentist_id: dentistId,
        dental_insurance_id: insuranceId,
        card_number: cardNumber,
        observations: observation,
      });

      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Erro ao atualizar status",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentAppointment) return;
    if (status === "attended" && isReviewExpired) {
      setReviewGateOpen(true);
      return;
    }
    await persistStatusChange();
  };

  const handleSavePatient = async (data: CreatePatientData, patientId?: string) => {
    if (!patientId || !appointment) return;

    setIsLoading(true);
    try {
      await updatePatient(patientId, data);
      const refreshed = await fetchAppointmentById(appointment.id);
      setLoadedAppointment(refreshed);
      toast({ title: "Paciente atualizado!" });
      setEditPatientDialogOpen(false);

      // Se o editor de paciente foi aberto pelo gate (status=atendido +
      // cadastro vencido), encerra a mudança de status que estava pendente.
      if (pendingAttendedSave) {
        setPendingAttendedSave(false);
        await persistStatusChange();
      } else {
        onSave(refreshed);
      }
    } catch (error) {
      toast({
        title: "Erro ao atualizar paciente",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
      throw error; // mantém o CreateEditPatientDialog aberto pra usuário corrigir
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendAuthorization = async () => {
    if (!currentAppointment || !authDescription.trim()) return;

    const clinicId = currentAppointment.clinic_id;
    const insuranceDentalId = insuranceId || currentAppointment.dental_insurance_id || currentAppointment.appointment_dental_insurance_id || "";

    if (!insuranceDentalId) {
      toast({ title: "Convênio não informado", description: "Selecione o convênio antes de solicitar a autorização.", variant: "destructive" });
      return;
    }

    setIsSendingAuth(true);
    try {
      await createAuthorizationRequest({
        appointment_id: currentAppointment.id,
        clinic_id: clinicId,
        dental_insurance_id: insuranceDentalId,
        description: authDescription.trim(),
      });
      toast({ title: "Autorização solicitada!", description: "Notificação enviada ao grupo do Telegram." });
      setAuthDescription("");
      setAuthExpanded(false);
    } catch (error) {
      toast({
        title: "Erro ao solicitar autorização",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSendingAuth(false);
    }
  };

  // Memoizado pra não criar nova referência a cada render do parent — senão o
  // useEffect interno do CreateEditPatientDialog dispara em re-renders (ex.:
  // depois de um save com erro) e sobrescreve o que o usuário acabou de digitar.
  const patientForEdit: Patient | null = useMemo(() => {
    if (!currentAppointment) return null;
    return {
      id: currentAppointment.patient_id,
      name: currentAppointment.patient_name,
      cpf: currentAppointment.patient_cpf,
      birth_date: currentAppointment.patient_birth_date || "",
      email: currentAppointment.patient_email,
      cellphone: currentAppointment.patient_cellphone,
      postcode: "", city_name: "", state_code: "",
      card_number: currentAppointment.patient_card_number || "",
      dental_insurance_id: currentAppointment.patient_dental_insurance_id || "",
      created_at: "", created_by: "", updated_at: "", updated_by: "", status: 1,
    };
  }, [
    currentAppointment?.patient_id,
    currentAppointment?.patient_name,
    currentAppointment?.patient_cpf,
    currentAppointment?.patient_birth_date,
    currentAppointment?.patient_email,
    currentAppointment?.patient_cellphone,
    currentAppointment?.patient_card_number,
    currentAppointment?.patient_dental_insurance_id,
  ]);

  if (!currentAppointment || !patientForEdit) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between border-b pb-2">
            <DialogTitle className="text-sm font-semibold uppercase tracking-wide">Editar Atendimento</DialogTitle>
            <div className="flex items-center gap-1.5 bg-muted px-2 py-1 rounded-md border mr-6">
              <span className="text-xs font-mono text-muted-foreground uppercase">ID:</span>
              <span className="text-xs font-bold font-mono">{currentAppointment.id.split('-')[0]}...</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 hover:bg-primary/10"
                onClick={() => copyToClipboard(currentAppointment.id, "ID do Agendamento Copiado!")}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </DialogHeader>

          {isLoading && !loadedAppointment ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-xs text-muted-foreground animate-pulse">Carregando informações...</p>
            </div>
          ) : (
            <div className={`space-y-3 py-1 transition-opacity ${isLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>

              {/* ÁREA 1: Dados do Agendamento */}
              <div className="p-3 bg-primary/5 border border-primary/10 rounded-lg space-y-2">
                <h3 className="text-xs font-semibold text-primary uppercase tracking-wide">Dados do Agendamento</h3>
                <div className="grid grid-cols-4 gap-3 items-start">
                  <div className="col-span-1 space-y-0.5">
                    <p className="text-xs text-muted-foreground">Data/Hora</p>
                    <p className="text-xs font-medium">
                      {currentAppointment.slot_date
                        ? `${new Date(`${currentAppointment.slot_date}T${currentAppointment.slot_time}`).toLocaleDateString('pt-BR')} às ${currentAppointment.slot_time?.slice(0, 5)}`
                        : "—"}
                    </p>
                  </div>
                  <div className="col-span-1 space-y-0.5">
                    <p className="text-xs text-muted-foreground">Import Key</p>
                    <p className="text-xs font-medium">{currentAppointment.import_id || "—"}</p>
                  </div>
                  <div className="col-span-2 space-y-0.5">
                    <p className="text-xs text-muted-foreground">Dentista Atual</p>
                    <p className="text-xs font-medium">{currentAppointment.dentist_name}</p>
                  </div>
                </div>
              </div>

              {/* ÁREA 2: Dados do Paciente */}
              <div className="p-3 bg-accent/5 border border-accent/10 rounded-lg space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xs font-semibold text-accent uppercase tracking-wide">Dados do Paciente</h3>
                    {isReviewExpired && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800">
                        Cadastro vencido
                      </span>
                    )}
                    {isReviewExpiringSoon && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                        Expira em {reviewDaysRemaining}d
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" className="btn-header" onClick={handleCopyPatientData} disabled={isLoading}>
                      <Copy className="h-3 w-3 mr-1" /> Copiar
                    </Button>
                    <Button variant="outline" size="sm" className="btn-header" onClick={() => setEditPatientDialogOpen(true)} disabled={isLoading}>
                      <Edit className="h-3 w-3 mr-1" /> Editar
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-4 space-y-0.5">
                    <p className="text-xs text-muted-foreground">Nome</p>
                    <p className="text-xs font-medium">{currentAppointment.patient_name}</p>
                  </div>
                  <div className="col-span-2 space-y-0.5">
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="text-xs truncate">{currentAppointment.patient_email || '—'}</p>
                  </div>
                  <div className="col-span-1 space-y-0.5">
                    <p className="text-xs text-muted-foreground">Nascimento</p>
                    <p className="text-xs">{currentAppointment.patient_birth_date?.split('-').reverse().join('/') || '—'}</p>
                  </div>
                  <div className="col-span-1 space-y-0.5">
                    <p className="text-xs text-muted-foreground">Telefone</p>
                    <p className="text-xs">{formatPhone(currentAppointment.patient_cellphone) || '—'}</p>
                  </div>
                  <div className="col-span-2 space-y-0.5">
                    <p className="text-xs text-muted-foreground">Convênio</p>
                    <p className="text-xs">{currentAppointment.patient_dental_insurance_name || 'Particular'}</p>
                  </div>
                  <div className="col-span-1 space-y-0.5">
                    <p className="text-xs text-muted-foreground">Carteirinha</p>
                    <p className="text-xs">{currentAppointment.patient_card_number || '—'}</p>
                  </div>
                  <div className="col-span-1 space-y-0.5">
                    <p className="text-xs text-muted-foreground">CPF</p>
                    <p className="text-xs">{formatCPF(currentAppointment.patient_cpf)}</p>
                  </div>
                </div>
              </div>

              {/* ÁREA 2.5: Autorização de Convênio */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                <button
                  type="button"
                  className="flex items-center justify-between w-full"
                  onClick={() => setAuthExpanded(v => !v)}
                >
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-blue-600" />
                    <h3 className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Solicitar Autorização</h3>
                  </div>
                  {authExpanded
                    ? <ChevronUp className="h-3.5 w-3.5 text-blue-600" />
                    : <ChevronDown className="h-3.5 w-3.5 text-blue-600" />}
                </button>

                {authExpanded && (
                  <div className="space-y-2 pt-1">
                    <div className="space-y-1">
                      <Label className="filter-label">Procedimentos solicitados</Label>
                      <Textarea
                        placeholder="Ex: extração 48 semi, resina 26 O, limpeza"
                        value={authDescription}
                        onChange={e => setAuthDescription(e.target.value)}
                        className="min-h-[60px] text-xs bg-white"
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        className="btn-header bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={handleSendAuthorization}
                        disabled={isSendingAuth || !authDescription.trim()}
                      >
                        {isSendingAuth ? (
                          <><Loader2 className="mr-1.5 h-3 w-3 animate-spin" />Enviando...</>
                        ) : (
                          <><ShieldCheck className="mr-1.5 h-3 w-3" />Enviar Solicitação</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* ÁREA 3: Campos Editáveis */}
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg space-y-3">
                <h3 className="text-xs font-semibold text-orange-800 uppercase tracking-wide">Alterar Informações</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="filter-label">Trocar Dentista</Label>
                    <Select value={dentistId} onValueChange={setDentistId}>
                      <SelectTrigger className="filter-input"><SelectValue placeholder="Selecionar dentista" /></SelectTrigger>
                      <SelectContent>
                        {dentists.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="filter-label">Status do Atendimento</Label>
                    <Select value={status} onValueChange={(v: AppointmentStatus) => setStatus(v)}>
                      <SelectTrigger className="filter-input"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(Object.keys(statusLabels) as AppointmentStatus[])
                          .filter(k => k !== 'unbooked')
                          .sort((a, b) => statusLabels[a].localeCompare(statusLabels[b]))
                          .map(k => <SelectItem key={k} value={k}>{statusLabels[k]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="filter-label">Convênio Dental</Label>
                    <InsuranceSelect value={insuranceId} onValueChange={setInsuranceId} />
                  </div>
                  <div className="space-y-1">
                    <Label className="filter-label">Carteirinha</Label>
                    <Input className="filter-input" value={cardNumber} onChange={e => setCardNumber(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="filter-label">Observações de Atendimento</Label>
                  <Textarea
                    value={observation}
                    onChange={e => setObservation(e.target.value)}
                    className="min-h-[60px] text-xs bg-white"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" size="sm" className="btn-header" onClick={() => onOpenChange(false)} disabled={isLoading}>
              Cancelar
            </Button>
            <Button size="sm" className="btn-header" onClick={handleSave} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  Salvando...
                </>
              ) : "Salvar Alterações"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <CreateEditPatientDialog
        patient={patientForEdit}
        open={editPatientDialogOpen}
        onOpenChange={setEditPatientDialogOpen}
        onSave={handleSavePatient}
      />

      <RegistryReviewGateDialog
        open={reviewGateOpen}
        onOpenChange={(o) => {
          setReviewGateOpen(o);
          if (!o) setPendingAttendedSave(false);
        }}
        patient={
          currentAppointment
            ? {
                id: currentAppointment.patient_id,
                name: currentAppointment.patient_name,
                cpf: currentAppointment.patient_cpf,
                birth_date: currentAppointment.patient_birth_date,
                cellphone: currentAppointment.patient_cellphone,
              }
            : null
        }
        onConfirmed={async () => {
          if (appointment) {
            try {
              const refreshed = await fetchAppointmentById(appointment.id);
              setLoadedAppointment(refreshed);
            } catch {
              // segue com o save mesmo se refetch falhar
            }
          }
          await persistStatusChange();
        }}
        onEdit={() => {
          setPendingAttendedSave(true);
          setEditPatientDialogOpen(true);
        }}
      />
    </>
  );
}